/**
 * Markdown/paste → atomic memory items.
 *
 * The chunker is the one engine every parser rides: sections → bullets →
 * sentences, heading context folded in, everything collapsed to single-line
 * facts of ≤ IMPORT_ITEM_MAX_CHARS so imported rows stay inside organic
 * observation norms (the fold silently no-ops when its token budget blows).
 */
import type { NamiEpistemic } from '../types/nami.js';
import { IMPORT_ITEM_MAX_CHARS, type MemoryImportItem } from './types.js';

export interface ChunkOptions {
  epistemic?: NamiEpistemic;
  observedAt?: string;
  /** Prepended verbatim to every item ("My assistant: "). */
  prefix?: string;
  /** Heading-driven epistemic override; wins over `epistemic` when it returns a value. */
  epistemicForHeading?: (heading: string | null) => NamiEpistemic | undefined;
  /**
   * When false, headings still drive epistemic + review grouping but are NOT
   * folded into the item text — dump-prompt replies carry self-contained
   * facts under section labels.
   */
  prefixHeadings?: boolean;
}

/** Headings too generic to add meaning as a prefix. */
const GENERIC_HEADINGS = new Set([
  'memory',
  'memories',
  'saved memories',
  'notes',
  'misc',
  'other',
  'general',
  'context',
  'summary',
]);

const MIN_ITEM_CHARS = 8;

function stripFrontmatter(text: string): string {
  if (!text.startsWith('---')) return text;
  const end = text.indexOf('\n---', 3);
  if (end === -1) return text;
  const rest = text.slice(end + 4);
  return rest.startsWith('\n') ? rest.slice(1) : rest;
}

function stripCodeFences(text: string): string {
  return text.replace(/^```[^\n]*\n[\s\S]*?^```\s*$/gm, '');
}

/** A dump pasted from another assistant usually arrives inside one outer fence. */
function unwrapOuterFence(text: string): string {
  const trimmed = text.trim();
  const match = /^```[^\n]*\n([\s\S]*?)\n?```$/.exec(trimmed);
  return match ? match[1]! : text;
}

export function cleanLine(line: string): string {
  return line
    .replace(/\[([^\]]*)\]\([^)]*\)/g, '$1') // unwrap inline links, keep the label
    .replace(/[*_`]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

const BULLET_RE = /^\s*(?:[-*+]|\d{1,3}[.)])\s+/;

function stripBullet(line: string): string {
  return line.replace(BULLET_RE, '');
}

function headingPrefix(heading: string | null): string {
  if (!heading) return '';
  const clean = cleanLine(heading).replace(/^#+\s*/, '');
  if (!clean || GENERIC_HEADINGS.has(clean.toLowerCase())) return '';
  return clean;
}

function splitSentences(text: string): string[] {
  return text.split(/(?<=[.?!])\s+/).filter((s) => s.trim().length > 0);
}

/** Pack sentences into chunks ≤ max; a single overlong sentence hard-truncates with an ellipsis. */
export function packToLimit(text: string, max: number): string[] {
  if (text.length <= max) return [text];
  const chunks: string[] = [];
  let current = '';
  for (const sentence of splitSentences(text)) {
    const piece = sentence.length > max ? `${sentence.slice(0, max - 1).trimEnd()}…` : sentence;
    if (!current) {
      current = piece;
    } else if (current.length + 1 + piece.length <= max) {
      current = `${current} ${piece}`;
    } else {
      chunks.push(current);
      current = piece;
    }
  }
  if (current) chunks.push(current);
  return chunks;
}

interface Block {
  text: string;
  heading: string | null;
}

/** Group section content into blocks: one per top-level bullet (nested bullets join), one per paragraph. */
function blocksFromMarkdown(text: string): Block[] {
  const blocks: Block[] = [];
  let heading: string | null = null;
  let open: string[] = [];
  let openIsBullet = false;

  const flush = () => {
    if (open.length > 0) blocks.push({ text: open.join(openIsBullet ? '; ' : ' '), heading });
    open = [];
    openIsBullet = false;
  };

  for (const raw of text.split('\n')) {
    const line = raw.replace(/\s+$/, '');
    if (/^#{1,6}\s/.test(line)) {
      flush();
      heading = line;
      continue;
    }
    if (line.trim() === '') {
      flush();
      continue;
    }
    const isBullet = BULLET_RE.test(line);
    const indented = /^\s{2,}/.test(line);
    if (isBullet && !indented) {
      flush();
      open = [stripBullet(line)];
      openIsBullet = true;
    } else if (openIsBullet && isBullet) {
      open.push(stripBullet(line).trim()); // nested bullet → '; ' join at flush
    } else if (open.length > 0) {
      open[open.length - 1] = `${open[open.length - 1]} ${line.trim()}`; // wrapped line continues its part
    } else {
      open = [line.trim()];
    }
  }
  flush();
  return blocks;
}

function toItems(blocks: Block[], sourceDetail: string, opts: ChunkOptions): MemoryImportItem[] {
  const items: MemoryImportItem[] = [];
  for (const block of blocks) {
    if (/^\[[^\]]*\]\([^)]*\)\s*$/.test(block.text.trim())) continue; // link-only line (index pointer)
    const cleaned = cleanLine(block.text);
    if (cleaned.length < MIN_ITEM_CHARS) continue;
    const prefix = headingPrefix(block.heading);
    const textPrefix = opts.prefixHeadings === false ? '' : prefix;
    const withHeading =
      textPrefix && !cleaned.toLowerCase().startsWith(textPrefix.toLowerCase()) ? `${textPrefix}: ${cleaned}` : cleaned;
    const withCustom = opts.prefix ? `${opts.prefix}${withHeading}` : withHeading;
    const epistemic = opts.epistemicForHeading?.(block.heading ? cleanLine(block.heading).replace(/^#+\s*/, '') : null) ?? opts.epistemic;
    const detail = prefix ? `${sourceDetail} · ${prefix}` : sourceDetail;
    for (const chunk of packToLimit(withCustom, IMPORT_ITEM_MAX_CHARS)) {
      items.push({
        text: chunk,
        ...(epistemic ? { epistemic } : {}),
        ...(opts.observedAt ? { observedAt: opts.observedAt } : {}),
        sourceDetail: detail,
      });
    }
  }
  return items;
}

export function chunkMarkdown(text: string, sourceDetail: string, opts: ChunkOptions = {}): MemoryImportItem[] {
  const body = stripCodeFences(stripFrontmatter(text));
  return toItems(blocksFromMarkdown(body), sourceDetail, opts);
}

/**
 * Paste mode is line-oriented: memory dumps arrive as lists, one fact per
 * line. Short lines ending in ':' ("Identity:", "Here is everything I
 * remember:") are section labels — they become heading context (category +
 * review grouping) but never survive as facts, and never pollute the fact
 * text (prefixHeadings off).
 */
export function chunkPaste(text: string, opts: ChunkOptions = {}): MemoryImportItem[] {
  const body = unwrapOuterFence(text);
  const blocks: Block[] = [];
  let heading: string | null = null;
  for (const raw of body.split('\n')) {
    const line = raw.trim();
    if (line === '') continue;
    if (/^#{1,6}\s/.test(line)) {
      heading = line;
      continue;
    }
    const unbulleted = stripBullet(line);
    if (line.length <= 60 && line.endsWith(':')) {
      heading = line.slice(0, -1);
      continue;
    }
    blocks.push({ text: unbulleted, heading });
  }
  return toItems(blocks, 'Pasted', { prefixHeadings: false, ...opts });
}
